/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQueryClient } from '@tanstack/react-query'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Country } from '../../../components/FormElements/Country'
import { Input } from '../../../components/FormElements/Input'
import {
    CountryCode,
    PhoneNumber,
    isValidPhone,
} from '../../../components/FormElements/PhoneNumber'
import { validateCI } from '../../../helpers/ciValidation'
import { useCountries } from '../../../hooks/queries/useCountries'
import { useLocalization } from '../../../hooks/queries/useLocalization'
import { useUpdateUser } from '../../../hooks/queries/useUpdateUser'
import { SignUpStyle } from './SignUpStep1'
import { useUser } from '../../../hooks/queries/useUser'
import { useUIContext } from '../../../context/UIContext'

export const SignUpStep2 = () => {
    const { showError } = useUIContext()
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const { t } = useTranslation('signUp')
    const { localization } = useLocalization()
    const { countries } = useCountries()
    const { user, isLoading } = useUser()
    const { updateAsync, api_error, isUpdating } = useUpdateUser()
    const [changes, setChanges] = useState<Partial<FormData>>({})
    const { countryId, name, lastname, phone, dni } = changes ?? {}

    const [defaultCountry, setDefaultCountry] = useState<CountryCode>(
        localization?.country_code as CountryCode
    )

    const DNIRequiredCountries = ['Uruguay']

    const isDNIRequired = countries?.some(
        ({ name, id }) => DNIRequiredCountries.includes(name) && id === (countryId ?? 0)
    )


    useEffect(() => {
        if (!user) return
        user && setChanges({
            name: user.name ?? '',
            lastname: user.lastname ?? '',
            phone: user.phone ?? '',
            dni: user.dni ?? undefined,
            countryId: user.countryId ?? '',
        })
    }, [user])

    const isReferrerInefop = user?.referrer?.trim().toLowerCase() === 'inefop'

    // Set countryId to Uruguay if referrer is INEFOP
    useEffect(() => {
        const uyCountryId = countries?.find(({ iso3 }) => iso3 === 'URY')?.id
        const locateCountryId = countries?.find(({ iso3 }) => iso3 === localization?.country_code_iso3)?.id
        if (isReferrerInefop && uyCountryId) return setChanges(prev => ({ ...prev, countryId: uyCountryId }))
        if (!countryId && locateCountryId) return setChanges(prev => ({ ...prev, countryId: locateCountryId }))

    }, [countries, countryId, isReferrerInefop, localization?.country_code_iso3])



    useEffect(() => {
        if (!countryId) return
        const country = countries?.find(({ id }) => id === countryId)
        country && setDefaultCountry(country.iso2 as CountryCode)
    }, [countryId, countries])

    const uyCountryId = countries?.find(({ name }) => name === 'Uruguay')?.id

    const onChange = ({
        target: { name, value },
    }: ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
        if (name === 'referrer' && value.toLowerCase() === 'inefop')
            setChanges(prev => ({ ...prev, countryId: uyCountryId }))

        setChanges(prev => ({ ...prev, [name]: name === 'countryId' ? +value : value }))
    }

    const isCompleted = name?.trim() && lastname?.trim() && countryId && phone?.trim()


    const onSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault()
        if (!isCompleted) return showError(t('Please complete all fields'))
        if (!dni) delete changes.dni
        await updateAsync(changes)
        queryClient.invalidateQueries().then(() => navigate('/'))
    }

    if (isLoading || isUpdating) return <Skeleton />

    return (
        <SignUpStyle>
            <h1 className="title">{t('Personal information')}</h1>
            {user?.email && (
                <div className="user-email">
                    <div className="email">
                        {t('Iniciaste como')} <span>{user?.email}</span>
                        <div className="logout-container">
                            <span>(</span>
                            <button
                                className="text outlined logout-button"
                                onClick={() => navigate('/logout')}
                            >
                                {t('Change')}
                            </button>
                            <span>)</span>
                        </div>
                    </div>
                </div>
            )}
            <form onSubmit={onSubmit}>
                <Input
                    {...{
                        label: t('Name'),
                        id: 'name',
                        name: 'name',
                        type: 'text',
                        autoComplete: 'given-name',
                        value: name ?? '',
                        onChange,
                        api_error,
                        autoFocus: true,
                    }}
                />
                <br />

                <Input
                    {...{
                        label: t('Surname'),
                        id: 'lastname',
                        name: 'lastname',
                        type: 'text',
                        autoComplete: 'family-name',
                        value: lastname ?? '',
                        onChange,
                        api_error,
                    }}
                />
                <br />

                <Country
                    {...{
                        value: countryId ?? '',
                        onChange,
                        api_error,
                        disabled: isReferrerInefop,
                    }}
                />
                {isDNIRequired && (
                    <>
                        <br />
                        <Input
                            {...{
                                label: t('C.I.'),
                                id: 'dni',
                                name: 'dni',
                                type: 'string',
                                value: dni ?? '',
                                onChange,
                                disabled: !!user?.dni,
                                api_error,
                                maxLength: 8,
                                error: dni && !validateCI(dni) ? t('Invalid C.I.') : undefined,
                            }}
                        />
                    </>
                )}
                <br />
                <PhoneNumber
                    {...{
                        label: t('What is your phone number?'),
                        defaultCountry,
                        value: (phone as any) ?? undefined,
                        onChange: (phone: any) =>
                            setChanges(prev => (prev ? { ...prev, phone: phone ?? null } : prev)),
                        error:
                            phone && !isValidPhone(phone ?? '')
                                ? t('Invalid phone number')
                                : undefined,
                    }}
                />
                <br />
                <button
                    className="primary"
                    children={t('Continue')}
                    style={{ width: '100%', margin: '0 auto' }}
                    disabled={!isCompleted}
                />
            </form>
        </SignUpStyle>
    )
}
export const Skeleton = () => {
    return (
        <SignUpStyle>
            <div className="skeleton title" />
            <br />
            <div className="skeleton text" />
            <br />
            <form>
                <div className="skeleton input" />
                <br />
                <div className="skeleton input" />
                <br />
                <div className="skeleton input" />
                <br />
                <div className="skeleton input" />
                <br />
                <div className="skeleton input" />
                <br />
                <div className="skeleton button" />
            </form>
        </SignUpStyle>
    )
}

// Interfaces
interface FormData {
    name: string
    lastname: string
    phone: string
    dni?: string
    countryId: number
}